import * as THREE from 'three';
import { gsap, Power1 } from 'gsap';
import particlesVert from '../../shaders/particles.vert';
import particlesFrag from '../../shaders/particles.frag';
import xRayVertex from '../shaders/xRay.vert';
import xRayFrag from '../shaders/xRay.frag';
import particlesShadowVert from '../../shaders/particlesShadow.vert';
import particlesShadowFrag from '../../shaders/particlesShadow.frag';
// import Chuncks from './chunks'; // Replaced by inline GLSL or new shader logic

class ParticleSystem {
    constructor(mainBrain, brainParticles, memories) {
        this.mainBrain = mainBrain;
        this.brainParticles = brainParticles;
        this.memories = memories;
        this.particlesStartColor = new THREE.Color(0xffffff);
        this.particlesColor = new THREE.Color(0xffffff);

        const { xRayEffect, systemPoints, shadowParticles } = this.init();
        this.particles = systemPoints;
        this.shadowParticles = shadowParticles;
        this.xRay = xRayEffect;
    }

    static getLoadingPoints() {
        const geometry = new THREE.RingGeometry(1, 0.4, 150, 20); // RingBufferGeometry -> RingGeometry
        // We need 3D points. RingGeometry gives 3D points (z=0).
        // Original was RingBufferGeometry(100, 40, 150, 150, 20)? InnerRadius, OuterRadius?
        // ThreeJS RingGeometry: innerRadius, outerRadius, thetaSegments, phiSegments, thetaStart, thetaLength
        // Old: RingBufferGeometry(innerRadius, outerRadius, thetaSegments, phiSegments, thetaStart, thetaLength)
        // 100, 40? No. 
        // Let's assume some reasonable values.
        return geometry.attributes.position.array;
    }

    init() {
        const duration = 1.0;
        const maxPointDelay = 0.3;

        const brainPoints = this.brainParticles.attributes.position.array;
        const count = brainPoints.length / 3;

        const geometry = new THREE.BufferGeometry();

        // Attributes
        const aStartLoading = [];
        const aEndPos = [];
        const aDelayDuration = [];
        const aStartColor = [];
        const aEndColor = [];
        const aRandom = [];

        // Loading Circle points
        // We generate points on a ring. Or random.
        // Simplified: Random points for loading
        for (let i = 0; i < count; i++) {
            // Start Position (Loading)
            // Start Position (Loading) - Starburst / Filled Sphere distribution
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos(2 * Math.random() - 1); // Spherical distribution
            const r = Math.pow(Math.random(), 1 / 3) * 200; // Cube root for uniform sphere, or just random for center bias

            // Convert spherical to cartesian for a 3D "star" burst
            aStartLoading.push(
                r * Math.sin(phi) * Math.cos(theta),
                r * Math.sin(phi) * Math.sin(theta),
                r * Math.cos(phi)
            );

            // End Position (Brain)
            aEndPos.push(brainPoints[i * 3], brainPoints[i * 3 + 1], brainPoints[i * 3 + 2]);

            // Delay
            aDelayDuration.push(Math.random() * maxPointDelay, duration);

            // Color (White)
            aStartColor.push(1.0, 1.0, 1.0);
            aEndColor.push(1.0, 1.0, 1.0);
            aRandom.push(Math.random());
        }

        geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(aEndPos), 3)); // Helper for bounding box?
        geometry.setAttribute('aStartLoading', new THREE.BufferAttribute(new Float32Array(aStartLoading), 3));
        geometry.setAttribute('aEndPos', new THREE.BufferAttribute(new Float32Array(aEndPos), 3));
        geometry.setAttribute('aDelayDuration', new THREE.BufferAttribute(new Float32Array(aDelayDuration), 2));
        geometry.setAttribute('aStartColor', new THREE.BufferAttribute(new Float32Array(aStartColor), 3));
        geometry.setAttribute('aEndColor', new THREE.BufferAttribute(new Float32Array(aEndColor), 3));
        geometry.setAttribute('aRandom', new THREE.BufferAttribute(new Float32Array(aRandom), 1));


        const material = new THREE.ShaderMaterial({
            uniforms: {
                uTime: { value: 0 },
                uProgress: { value: 0.0 },
                uAngle: { value: 1.0 },
                uPointSizeEffect: { value: 0.1 },
                uColor: { value: new THREE.Color(0xffffff) },
            },
            vertexShader: particlesVert,
            fragmentShader: particlesFrag,
            transparent: true,
            blending: THREE.AdditiveBlending,
            depthTest: false,
            depthWrite: false,
        });

        const systemPoints = new THREE.Points(geometry, material);
        systemPoints.castShadow = true;
        systemPoints.frustumCulled = false;

        const shadowMaterial = material.clone();
        shadowMaterial.uniforms = THREE.UniformsUtils.clone(material.uniforms);
        shadowMaterial.blending = THREE.NormalBlending;
        shadowMaterial.uniforms.uColor.value.set(0x888888); // Lighter Grey
        shadowMaterial.uniforms.uShadowOpacity = { value: 0.0 }; // Start invisible
        shadowMaterial.vertexShader = `
            uniform float uTime;
            uniform float uProgress;
            uniform float uPointSizeEffect;
            uniform float uAngle;

            attribute vec2 aDelayDuration;
            attribute vec3 aStartLoading;
            attribute vec3 aEndPos;
            attribute vec3 aStartColor;
            attribute vec3 aEndColor;

            varying vec3 vColor;

            float easeExpoInOut(float t) {
                return t == 0.0 ? 0.0 : t == 1.0 ? 1.0 : t < 0.5 ? pow(2.0, 20.0 * t - 10.0) / 2.0 : (2.0 - pow(2.0, -20.0 * t + 10.0)) / 2.0;
            }

            void main() {
                float tProgress = clamp(uProgress - aDelayDuration.x, 0.0, aDelayDuration.y) / aDelayDuration.y;
                tProgress = easeExpoInOut(tProgress);

                vec3 transformed = vec3(0.0);

                if(tProgress < 0.5){ 
                     vec3 pos = aStartLoading;
                     transformed = mix(pos * 3.0, pos, tProgress * 2.0);
                } else {
                     transformed = mix(aStartLoading, aEndPos, tProgress);
                }
                
                vColor = vec3(0.0); // Black for shadow (absorbs light, no bloom)
                
                // Shadow doesn't get aRandom? We need to declare it in vertex shader if we want to silence warnings, or just ignore.
                // It's part of the geometry, so it IS attached. We just need to define it in shader if used.
                // Not used in shadow shader.

                vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
                gl_PointSize = (1200.0 / -mvPosition.z); 
                gl_Position = projectionMatrix * mvPosition;
            }
        `;
        shadowMaterial.fragmentShader = `
            uniform float uShadowOpacity;
            varying vec3 vColor;
            void main() {
                float dist = length(gl_PointCoord - vec2(0.5));
                if (dist > 0.5) discard;
                gl_FragColor = vec4(vColor, uShadowOpacity); 
            }
        `;

        // We need a separate mesh for shadow.
        const shadowParticles = new THREE.Points(geometry, shadowMaterial);
        shadowParticles.castShadow = false;
        shadowParticles.receiveShadow = false;
        shadowParticles.frustumCulled = false;
        shadowParticles.position.y = -160; // Floor level relative to origin
        shadowParticles.scale.y = 0.001; // Flatten
        shadowParticles.visible = true; // Always visible, controlled by opacity

        // XRay
        const xRayMaterial = new THREE.ShaderMaterial({
            uniforms: {
                c: { type: 'f', value: 0.9 },
                p: { type: 'f', value: 6.7 },
                glowColor: { type: 'c', value: new THREE.Color(0x84ccff) },
                viewVector: { type: 'v3', value: new THREE.Vector3(0, 0, 0) },
                lightningTexture: { type: 't', value: this.mainBrain.loaders.brainXRayLight },
                offsetY: { type: 'f', value: 0.3 },
                uTime: { type: 'f', value: 0.0 },
            },
            vertexShader: xRayVertex,
            fragmentShader: xRayFrag,
            side: THREE.DoubleSide,
            blending: THREE.AdditiveBlending,
            transparent: true,
            depthWrite: false,
        });

        // For XRay geometry, we need the brain geometry.
        // this.mainBrain.endPointsCollections is a BufferGeometry.
        // We can just use it.
        const xRayGeometry = this.mainBrain.endPointsCollections.clone();

        const xRayEffect = new THREE.Mesh(xRayGeometry, xRayMaterial);

        return { xRayEffect, systemPoints, shadowParticles };
    }

    update(deltaTime, camera, brain) {
        this.particles.material.uniforms.uTime.value = deltaTime;
        if (this.xRay && this.xRay.material) {
            this.xRay.material.uniforms.viewVector.value = new THREE.Vector3().subVectors(camera.position, brain.position); // Brain doesn't have position? Brain is group? 
            // In MainBrain, brain is added to scene? No, addBrain adds meshes to scene directly?
            // Wait, this.xRay is passed as 'brain' argument in MainBrain.js?
            // MainBrain.js: this.particlesSystem.update(this.deltaTime, this.camera, this.particlesSystem.xRay);
            // So brain arg IS xRay mesh. xRay mesh position is (0,0,0) by default.
            this.xRay.material.uniforms.uTime.value = deltaTime;
            this.xRay.material.uniforms.uTime.value = deltaTime;
        }
        if (this.shadowParticles) {
            this.shadowParticles.material.uniforms.uTime.value = deltaTime;
            this.shadowParticles.material.uniforms.uProgress.value = this.particles.material.uniforms.uProgress.value;
        }
    }

    isXRayActive(status) {
        if (status) {
            const progress = { p: 0.0 };
            gsap.fromTo(progress, { p: 3.0 }, {
                p: 5.0,
                duration: 3.0,
                ease: Power1.easeIn,
                onUpdate: () => {
                    this.xRay.material.uniforms.offsetY.value = Math.sin(progress.p);
                },
            });
        } else {
            const progress = { p: 1.0 };
            gsap.fromTo(progress, { p: 5.0 }, {
                p: 3.0,
                duration: 3.0,
                ease: Power1.easeIn,
                onUpdate: () => {
                    this.xRay.material.uniforms.offsetY.value = Math.sin(progress.p);
                },
            });
        }
    }

    transform(status) {
        if (status) { // Start Intro
            const progress = { p: 0.0 };
            gsap.fromTo(progress, { p: 0.0 }, {
                p: 1.5,
                duration: 5.9,
                ease: Power1.easeIn,
                onUpdate: () => {
                    this.updateTransitioning(progress.p);
                },
                onComplete: () => {
                    this.mainBrain.orbitControls.maxDistance = 700;
                    this.mainBrain.orbitControls.autoRotate = true;
                    this.updateTransitioning(1.5);
                    // Shadow opacity is handled by the tween below
                },
            });

            // Animate Shadow Opacity
            if (this.shadowParticles) {
                const shadowProgress = { p: 0.0 };
                gsap.to(shadowProgress, {
                    p: 0.25, // Target opacity (lighter)
                    duration: 5.0,
                    delay: 2.0, // Start fading in midway
                    ease: Power1.easeIn,
                    onUpdate: () => {
                        if (this.shadowParticles.material.uniforms.uShadowOpacity) {
                            this.shadowParticles.material.uniforms.uShadowOpacity.value = shadowProgress.p;
                        }
                    }
                });
            }
        }
    }

    updateTransitioning(val) {
        this.particles.material.uniforms.uProgress.value = val;
    }
}

export default ParticleSystem;
